import React, { useState, useEffect, useRef, useCallback } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaHourglassEnd, FaHistory, FaTimes } from 'react-icons/fa';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import './DetectiveQuiz.css';
import '../../ui/ArcadeShared.css';

const DetectiveQuiz = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  // Состояния данных игры
  const [state, setState] = useState(null); // Текущий вопрос
  const [finalData, setFinalData] = useState(null); // Данные финиша

  // Состояния UI
  const [inputValue, setInputValue] = useState('');
  const [selectedOptionIdx, setSelectedOptionIdx] = useState(null);
  const [isPending, setIsPending] = useState(false); // Ожидание ответа сервера

  // ФИДБЕК: Хранит данные о проверке последнего ответа
  // { isCorrect, correctAnswer, textWithAnswer, ... }
  const [feedback, setFeedback] = useState(null);

  // Состояние просмотра истории
  const [showHistory, setShowHistory] = useState(false);

  const inputRef = useRef(null);

  // Логика перехода: Показать фидбек -> Пауза -> Новый вопрос
  const handleFeedbackTransition = useCallback((feedbackData, nextState, finishData) => {
    setFeedback(feedbackData); // Это триггерит ререндер с подсветкой и новым текстом

    // Вибрация
    window.croco?.haptic?.(feedbackData.isCorrect ? 'success' : 'warning');

    // Пауза 2.5 секунды, чтобы игрок изучил "расшифрованную" сетку
    setTimeout(() => {
       if (finishData) {
          setFinalData(finishData);
       } else if (nextState) {
          setState(nextState);
          resetStep();
          // Если следующий вопрос требует ввода, ставим фокус
          if (nextState.question.type === 'input') {
             setTimeout(() => inputRef.current?.focus(), 50);
          }
       }
    }, 1500);
  }, []);

  useEffect(() => {
    if (!socket) return;

    // Запуск игры
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    const handleEvent = (data) => {
      setIsPending(false); // Ответ получен

      if (data.type === 'update' || data.type === 'next') {
        // Если есть feedback (мы ответили на вопрос), показываем результат
        if (data.feedback) {
           handleFeedbackTransition(data.feedback, data.state);
        } else {
           // Просто инициализация или первый вопрос
           setState(data.state);
           resetStep();
        }
      }
      else if (data.type === 'finish') {
         // Конец игры, показываем фидбек по последнему вопросу
         handleFeedbackTransition(data.feedback, null, data);
      }
      else if (data.type === 'game_over') {
         // Принудительное завершение игры (таймер истек)
         setFinalData(data);
      }
    };

    socket.on('arcade:event', handleEvent);
    return () => socket.off('arcade:event', handleEvent);
  }, [socket, handleFeedbackTransition, store.ref.work.methods]);

  // Сброс UI перед новым вопросом
  const resetStep = () => {
    setInputValue('');
    setSelectedOptionIdx(null);
    setFeedback(null);
  };

  const submitAnswer = (val, idx = null) => {
     if (isPending || feedback) return; // Блокировка

     setIsPending(true);
     if (idx !== null) setSelectedOptionIdx(idx);

     store.ref.work.methods.hostAction('mafiaMiniGameAction', {
        type: 'answer',
        value: val
     });
  };


  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // --- Рендер "Анализа ошибок" (Истории) ---
  const renderHistory = () => {
    // История может быть в finalData.my.history (от финиша)
    // или в state.history (если мы передали её в процессе)
    const historyList = finalData?.my?.history  || [];

    return (
        <div className="quiz-history-overlay">
            <div className="history-header-title">
                📂 {t('arcade.quiz_history_title', 'ДЕЛО №42')}
            </div>

            <div className="history-list add-scroll touch-active">
                {historyList.map((item, i) => (
                    <div key={i} className={`history-item ${item.isCorrect ? 'correct' : 'wrong'}`}>
                        {/* Самое важное: показываем textWithAnswer (расшифрованную сетку) */}
                        <div className="history-q-text" dangerouslySetInnerHTML={{ __html: item.textWithAnswer || item.questionText }} />

                        <div className="history-answer-row">
                            <span>
                                <span className="ans-label">{t('arcade.quiz_your_answer', 'Вы:')}</span>
                                <span className={`ans-val ${item.isCorrect ? 'good' : 'bad'}`}>
                                    {item.userAnswer}
                                </span>
                            </span>
                            {!item.isCorrect && (
                                <span>
                                    <span className="ans-label">{t('arcade.quiz_correct', 'Верно:')}</span>
                                    <span className="ans-val good">
                                        {item.correctAnswer}
                                    </span>
                                </span>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <button className="quiz-close-btn" style={{marginTop: '15px', border: '1px solid #333'}} onClick={() => setShowHistory(false)}>
                <FaTimes style={{marginRight: '8px'}}/> {t('arcade.back', 'Назад')}
            </button>
        </div>
    );
  };

  // --- Рендер Финального Экрана ---
  if (finalData) {
      if (showHistory) return renderHistory();

      const myStats = finalData.my || finalData.finalData?.my || {};

      return (
        <div className="quiz-container">
           <div className="quiz-header">
              <div className="quiz-title">{t('arcade.quiz_report', 'ОТЧЕТ')}</div>
           </div>
           <div className="quiz-final">
              <div>{t('arcade.quiz_completed', 'Тестирование завершено')}</div>

              <div className="final-score">
                 {myStats.score > 0 ? '+' : ''}{myStats.score}
              </div>

              <div style={{opacity: 0.7}}>
                 {t('arcade.quiz_time', { time: (myStats.timeSpent/1000).toFixed(1) })}c
              </div>
           </div>

           {/* ДОБАВЛЯЕМ РЕЙТИНГ */}
           <UnifiedLeaderboard
              results={finalData.leaderboard}
              myId={store.ref.telegram.telegramUserId}
              t={t}
           />

           <div style={{marginTop: 'auto', width: '100%', display: 'flex', flexDirection: 'column', gap: '8px'}}>
               <button className="quiz-history-btn" onClick={() => setShowHistory(true)}>
                   <FaHistory style={{marginRight: '8px'}} />
                   {t('arcade.quiz_analyze', 'Анализ ошибок')}
               </button>

               <button className="quiz-close-btn" style={{width: '100%', justifyContent: 'center'}} onClick={onClose}>
                   {t('arcade.close_dossier', 'Закрыть дело')}
               </button>
           </div>
        </div>
      );
  }

  // --- Загрузка ---
  if (!state) return null;

  // --- Рендер Вопроса ---
  const { question, step, total } = state;

  // ЛОГИКА ТЕКСТА:
  // Если есть feedback, мы подменяем текст вопроса на "расшифрованный" (textWithAnswer).
  // Это создает эффект, что сетка "раскрылась" после ответа.
  const displayText = feedback ? (feedback.textWithAnswer || question.text) : question.text;
  const isCorrectFeedback = feedback?.isCorrect;

  return (
    <div className="quiz-container">
       <div className="quiz-timer" style={{color: timeLeft < 10 ? '#ff4444' : '#f4e4bc'}}>
          {formatTime(timeLeft)}
       </div>

       <div className="quiz-header">
          <div className="quiz-title">{t('arcade.quiz_title', 'ДОПРОС')}</div>
          <div className="quiz-progress">№ {step} / {total}</div>
       </div>

       <div
         className="quiz-question-text"
         // Рендерим HTML, так как текст содержит <br/> и возможно другие теги
         dangerouslySetInnerHTML={{ __html: displayText }}
       />

       {question.type === 'choice' ? (
          <div className="quiz-options-list">
             {question.options.map((opt, idx) => {
                let btnClass = "quiz-btn";

                if (isPending) {
                   // Ожидание ответа
                   if (idx === selectedOptionIdx) btnClass += " pending";
                   else btnClass += " disabled";
                } else if (feedback) {
                   // Результат
                   // 1. Это правильный ответ? (Всегда подсвечиваем)
                   if (String(opt) === String(feedback.correctAnswer)) {
                       btnClass += " reveal-correct";
                   }
                   // 2. Это выбранный нами, но неправильный ответ?
                   else if (idx === selectedOptionIdx && !isCorrectFeedback) {
                       btnClass += " reveal-wrong";
                   }
                   // 3. Остальные
                   else {
                       btnClass += " disabled";
                   }
                }

                return (
                  <button
                    key={idx}
                    className={btnClass}
                    onClick={() => submitAnswer(opt, idx)}
                    disabled={isPending || feedback} // Блокируем
                  >
                     <span dangerouslySetInnerHTML={{ __html: `${idx + 1}. ${opt}` }} />
                  </button>
                );
             })}
          </div>
       ) : (
          <form className="quiz-input-row" onSubmit={(e) => { e.preventDefault(); if(inputValue.trim()) submitAnswer(inputValue); }}>
             <input
               ref={inputRef}
               // Подсветка инпута
               className={`quiz-input ${feedback ? (isCorrectFeedback ? 'correct' : 'incorrect') : ''}`}
               value={inputValue}
               onChange={(e) => setInputValue(e.target.value)}
               placeholder={question.placeholder || '...'}
               disabled={isPending || feedback}
               autoFocus
             />
             <button type="submit" className="quiz-submit-btn" disabled={isPending || feedback}>
                {isPending ? <FaHourglassEnd /> : <FaArrowRight />}
             </button>
          </form>
       )}

       {/* Штамп (ВЕРНО/ОШИБКА) */}
       {feedback && (
          <div className="quiz-stamp-overlay">
             <div className={`stamp ${isCorrectFeedback ? 'stamp-success' : 'stamp-fail'}`}>
                {isCorrectFeedback ? t('arcade.stamp_approved', 'ВЕРНО') : t('arcade.stamp_rejected', 'ОШИБКА')}
             </div>
          </div>
       )}
    </div>
  );
};

export default DetectiveQuiz;